import React, { useRef, useState, useContext, useEffect } from "react";
import { Card, Button, Col, Form, Row, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { abpnClient } from "../../../../Services/AbpnClient";
import { CreditCardClientLibrary, getCreditCardProcessor, getPaymentProcessorForPaymentType } from "../../../../Services/PaymentProcessor/PaymentProcessor";
import Payment from "../../../../Services/PaymentProcessor/Payment";
import { Address } from "../../../Physician/Models/Address";
import { AuthContext } from "../../../Auth/AuthProvider";
import { CCAnnualFee, CCBlock } from "../../../Physician/Models/CCBlock";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import CCAnnualFeePayment from "../Models/CCAnnualFeePayment";
import { PaymentContainer } from "../../../../Services/PaymentProcessor/PaymentContainer";
import CCAnnualFeePaymentResponse from "../Models/CCAnnualFeePaymentResponse";
import { configService } from "../../../../Services/Config/ConfigService";
import { booleanify } from "../../../Shared/Utility/Booleanify";
import InvoiceModel from "../../../Shared/Models/InvoiceModel";
import ReceiptModel from "../../../Shared/Models/ReceiptModel";
import moment from "moment";
import { usePrevious } from "../../../Shared/Utility/usePrevRefHook";
import { useHistory } from "react-router-dom";

interface IProps {
    ccBlock: CCBlock;
    onSaveBlock: (updatedBlock: CCBlock) => void;
    isReadOnly: boolean;
    blockYears: Array<number>;
    contactAddress: Address;
    email: string;
}

export function AnnualFees(props: IProps) {

    const [payFees, setPayFees] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedYearsToPay, setSelectedYearsToPay] = useState<Array<CCAnnualFee>>([]);
    const [payment, setPayment] = useState<Payment>({ email: props.email, city: props.contactAddress?.city, state: props.contactAddress?.stateCode } as Payment);
    const [isPaymentSuccessful, setIsPaymentSuccessful] = useState<boolean>(null);
    const [useBC, setUseBC] = useState<boolean>(false);
    const [creditCardProcessor, setCreditCardProcessor] = useState<CreditCardClientLibrary>(CreditCardClientLibrary.None);
    const history = useHistory();

    let isPaymentChargedSubmitted = usePrevious(false); //track whether to submit to invoice if payment has been charged

    const authContext = useContext(AuthContext);
    const cardRef = useRef(null);
    const payContainerRef = React.useRef(null);
    const scrollToTop = () => cardRef?.current.scrollIntoView({ behavior: "smooth" })
    var unpaidFees = props.ccBlock.ccAnnualFees.filter(_ => !_.isWaived && !_.isPaid);

    const getUnpaidYears = () => {
        return unpaidFees.filter(_ => !props.ccBlock.unpaidOrphanYears.some(x => x == _.year));
    }

    useEffect(() => {
        const addRequiredFeesToPay = () => {
            const updated = deepCopy(selectedYearsToPay);
            var allAnnualFees = props.ccBlock.ccAnnualFees
            for (var i = 0; i < allAnnualFees.length; i++){
                var annualFee = allAnnualFees[i];
                var isOrphan = props.ccBlock.unpaidOrphanYears.some(_ => _ == annualFee.year);
                if (isOrphan && updated.every(_ => _.year != annualFee.year)) updated.push(annualFee);
            }

            if (updated.length === 0) {
                var unpaid = props.ccBlock.ccAnnualFees.filter(_ => !_.isWaived && !_.isPaid && !isSelectYearToPayInTheFuture(_) && !(_.year-1 === props.ccBlock.pendingExamYear));
                var requiredFee = unpaid[0];
                if (requiredFee){
                    updated.push(requiredFee)
                }
                
            }

            setSelectedYearsToPay(updated);
        }

        addRequiredFeesToPay();
    }, [props.ccBlock]);

    useEffect(() => {
        if (creditCardProcessor !== CreditCardClientLibrary.None) return;
        
        const getCreditCardProcessorForPaymentContainer = async () => {
            const cardProcessor : CreditCardClientLibrary = await getCreditCardProcessor();
            setCreditCardProcessor(cardProcessor);
        }
    
        getCreditCardProcessorForPaymentContainer();
    
    }, [])
    

    useEffect(() => {
        const getUseBC = async () => {
            let k = (await configService.getConfiguration()).FeatureFlags.UseBusinessCentral;
            if (typeof k === "string")
            {
                setUseBC(booleanify(k));
            }
            else {
                setUseBC(k);
            }

        }
        getUseBC();
    }, []);

    const onPaymentChanged = (updated: Payment) => {
        setPayment(deepCopy(updated));
    }

    const isSelectYearToPayInTheFuture = (annualFee: CCAnnualFee) => {
        return annualFee.year > moment().year();
    }

    const isSelectYearToPayDisabled = (annualFee: CCAnnualFee) => {
        if (props.isReadOnly) return true;
        if (isSelectYearToPayInTheFuture(annualFee)) return true;

        var unPaidYears = getUnpaidYears();
        var unSelectedYearsToPay = unPaidYears.filter(_ => !selectedYearsToPay?.some(x => x.year == _.year));

        var lastSelectedFee = selectedYearsToPay[selectedYearsToPay.length - 1];

        if (lastSelectedFee != null && lastSelectedFee.year !== annualFee.year)
        {
            if (selectedYearsToPay.some((selectedYear) => selectedYear.year === annualFee.year && selectedYear.feeAmount === annualFee.feeAmount && selectedYear.isPaid === annualFee.isPaid && selectedYear.isWaived === annualFee.isWaived)){
                return true
            }
        }

        if (unSelectedYearsToPay?.length > 0 && unSelectedYearsToPay.includes(annualFee) && unSelectedYearsToPay[0] !== annualFee) return true;

        return false;
    }

    const onFormSubmitPayment = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        e.currentTarget.classList.add('was-validated');

        if (!payContainerRef.current.validate(payment) || !e.currentTarget.checkValidity()) {
            setIsSubmitting(false);
            return;
        }

        e.currentTarget.classList.remove('was-validated');

        await submitPayment();
    }

    const refreshPaymentAmount = () => {
        onPaymentChanged({...payment, amount: getTotalAmount()});
    }

    useEffect(() => {
        refreshPaymentAmount();
    }, [selectedYearsToPay]);

    //Automatically submit the payment form is the payment was already charged through the payment processor client library
    useEffect(() => {
        async function checkShouldSubmitAfterPaymentCharged(){
            if (isPaymentChargedSubmitted.current == false && payment.hasBeenCharged == true){
                isPaymentChargedSubmitted.current = true;
                try{
                    await submitPayment();
                }
                finally{
                    resetPaymentCharge();
                }
            }
        }

        checkShouldSubmitAfterPaymentCharged();
    }, [payment.hasBeenCharged, isPaymentChargedSubmitted]);

    const resetPaymentCharge = () => {
        isPaymentChargedSubmitted.current = false;
        setPayment({...payment, hasBeenCharged: false, paymentCharge: null});
    }

    /**Handle payment processing errors with redirect to error page with custom error message */
    const handlePaymentError = () => {
        history.replace("/error/PaymentProcessingError");
    }

    const submitPayment = async () => {
        if (isSubmitting) return;
        try{
            
            setIsSubmitting(true);

            var updatedPayment = deepCopy(payment);
            const paymentProcessor = getPaymentProcessorForPaymentType(creditCardProcessor, updatedPayment.paymentType);
            updatedPayment = await paymentProcessor.tokenize(updatedPayment);
            updatedPayment.amount = getTotalAmount();

            setPayment(updatedPayment);

            if (!updatedPayment.IsValid) {
                setIsSubmitting(false);
                return;
            }

            var dto = new CCAnnualFeePayment(selectedYearsToPay, updatedPayment);

            var response : any;
            if (useBC){
                // Create Invoices
                const newInvoices : any = await abpnClient.post<InvoiceModel[]>('/api/invoice/createInvoicesForAnnualFeeYears', dto.years, authContext, { handleErrorRedirect: false })
                    .catch(handlePaymentError);
                // Apply Payment To Invoices
                let invoiceNumbers = newInvoices.map((_ : any) => _.invoiceNumber);

                const invoicePaymentRequest = {
                    invoiceNumbers: invoiceNumbers,
                    paymentCharge: payment.paymentCharge
                };
                
                await abpnClient.post<ReceiptModel>(`/api/invoice/applyPaymentToInvoices?paymentType=${updatedPayment.paymentType}&paymentProcessor=${paymentProcessor.getPaymentProcessorId()}&paymentToken=${updatedPayment.PaymentToken}&paymentAmount=${updatedPayment.amount}`, invoicePaymentRequest, authContext, { handleErrorRedirect: false })
                    .catch(handlePaymentError)
                    // Apply Payment To Invoices
                    .then(async (_ : any) => {
                        if (_ && _.isSuccessful) {
                            response = await abpnClient.post<CCAnnualFeePaymentResponse>(`/api/Physician/recordAnnualFeesPaid`, dto, authContext)
                            .catch(handlePaymentError)
                            setIsPaymentSuccessful(_.isSuccessful);
                        } else {
                            setIsSubmitting(false);
                            return;
                        }
                    })
            }else{
                response = await abpnClient.post<CCAnnualFeePaymentResponse>(`/api/Physician/PayCCAnnualFees`, dto, authContext, { handleErrorRedirect: false })
                    .catch(handlePaymentError)
                setIsPaymentSuccessful(response.isSuccessful);
                if (!response.isSuccessful) {
                    setIsSubmitting(false);
                    return;
                }
            }
            
            setTimeout(() => setIsPaymentSuccessful(null), 5000);

            setPayFees(false);
            scrollToTop();
            setSelectedYearsToPay([]);

            props.onSaveBlock(response.ccBlock);
            setIsSubmitting(false);
        }
        catch {
            handlePaymentError();
        }
    }    

    const updateTotal = (annualFee: CCAnnualFee) => {
        const updated = deepCopy(selectedYearsToPay);
        const index = updated.findIndex(_ => _.year == annualFee.year);
        if (index >= 0) {
            updated.splice(index, 1);
            setSelectedYearsToPay(updated);
            return;
        }
        updated.push(annualFee);
        setSelectedYearsToPay(updated);
    }

    const getTotalAmount = () => {
        return selectedYearsToPay.map(_ => _.feeAmount).reduce((a, b) => a + b, 0);
    }

    const getPaidStatus = (year: number) => {
        var annualFee = props.ccBlock.ccAnnualFees.find(_ => _.year == year)

        if(annualFee != null){
            if (annualFee.isPaid) return "Paid";
            if (annualFee.isWaived) return "Waived";
    
            return `Not Paid: $${annualFee.feeAmount}`
        }
    }

    if (props.ccBlock == null) return <AbpnSpinner></AbpnSpinner>;

    const isSubmitDisabled = isSubmitting || selectedYearsToPay.length <= 0 || props.isReadOnly;

    return (
        <Card className="mb-4" border="pp-secondary-grey" ref={cardRef}>
            {isPaymentSuccessful && <Alert className="m-0 font-weight-bolder" variant="pp-alert-success" >Payment Received Successfully.</Alert>}
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder">
                    <span className="text-pp-primary">Annual Fees</span>
                    <OverlayTrigger
                        placement={"right"}
                        overlay={<Tooltip id="annualFeesTooltip" bsPrefix="pp-tooltip">
                            Annual fees cover either assessment pathway (Article Based Continuing Certification (ABCC) pathway or Examination pathway) for all active certifications in the CC Program.<br />
                            Annual fees include development and administration of both CC assessment pathways, reporting of Board certification status, access to personalized Physician Portal account, ongoing credentialing, and verification of certification.<br />
                            Annual fees are dependent on number of active CC certifications.  The annual fee is non-refundable.
                        </Tooltip>}>
                        <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                    </OverlayTrigger>
                </Card.Title>
                <Row className="mt-3">
                    <Col xs={12} md={12} lg={12} xl={12}>
                        <Form noValidate onSubmit={(e) => onFormSubmitPayment(e)} key={props.ccBlock.ccAnnualFees.toString()}>
                            <Row>
                                {
                                    props.blockYears.map(year => {
                                        return (<Col key={year}>
                                            <Form.Group controlId={year.toString()}>
                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">{year}</Form.Label>
                                                <Form.Text>{getPaidStatus(year)}</Form.Text>
                                            </Form.Group>
                                        </Col>)
                                    })
                                }
                            </Row>
                            <Card bsPrefix="border-0 pp-align-self-end mt-2">
                                <Button variant="pp-primary" disabled={props.isReadOnly} hidden={payFees || (getUnpaidYears().length === 0 && selectedYearsToPay.length === 0)} onClick={() => setPayFees(true)}>Pay Annual Fee</Button>
                            </Card>
                            {
                                payFees &&
                                <React.Fragment key={selectedYearsToPay.toString()}>
                                    <div className="pp-grey-line-break"></div>
                                    <div>
                                        <Form.Label className="text-pp-primary pp-font-size-smaller font-weight-bolder mt-3 mb-3">Pay Annual Fee</Form.Label>
                                        {props.ccBlock.unpaidOrphanYears?.length > 0 && props.ccBlock.unpaidOrphanYears.map((year: number ) => {
                                            var annualFee = props.ccBlock.ccAnnualFees.find(_ => _.year == year);
                                            return (<Form.Check
                                                type="checkbox"
                                                className="mb-3"
                                                checked={!!selectedYearsToPay.find(_ => _.year == year)}
                                                disabled={true}
                                                id={year.toString()}
                                                onChange={() => updateTotal(annualFee)}
                                                label={<span><b>{year}</b> (${annualFee.feeAmount}) **Unpaid Fee prior to block reset</span>}
                                            />)
                                        })}
                                        {getUnpaidYears().map(annualFee => {
                                            return (<Form.Check
                                                type="checkbox"
                                                className="mb-3"
                                                disabled={(isSelectYearToPayDisabled(annualFee)) || (annualFee.year-1 === props.ccBlock.pendingExamYear)}
                                                checked={selectedYearsToPay.some(_ => _.year == annualFee.year) && !isSelectYearToPayInTheFuture(annualFee) && !(annualFee.year-1 === props.ccBlock.pendingExamYear) }
                                                id={annualFee.year.toString()}
                                                onChange={() => updateTotal(annualFee)}
                                                label={<span><b>{annualFee.year}</b> (${annualFee.feeAmount}) {(annualFee.year-1 === props.ccBlock.pendingExamYear && "Payment can be made after grades are released for your pending examination") || (isSelectYearToPayInTheFuture(annualFee) && "* Payment can be made beginning 1/1/" + annualFee.year)}</span>}
                                            />)
                                        })}
                                        <div className="font-weight-bolder pp-border-top pt-2">Total ${getTotalAmount()}</div>
                                    </div>
                                    {payment.paymentType && payment.IsValid === false &&
                                        <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                            <p><b>Could Not Validate Payment Method.</b> Please verify your payment method and try again</p>
                                        </Alert>
                                    }
                                    {isPaymentSuccessful === false &&
                                        <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                            <p><b>The provided payment method failed.</b> Please verify your payment method and try again</p>
                                        </Alert>}
                                    {selectedYearsToPay.length > 0 && <PaymentContainer ref={payContainerRef} payment={payment} onPaymentChanged={onPaymentChanged} onPaymentTypeChanged={() => null} disabled={isSubmitDisabled} />}
                                    {isSubmitting ? <AbpnSpinner/> : null }
                                </React.Fragment>
                            }
                        </Form>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );

}